import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Shuffle as ShuffleIcon,
  Favorite as HeartIcon,
  FavoriteBorder as HeartBorderIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { doc, getDoc, setDoc, updateDoc, increment } from 'firebase/firestore'
import toast from 'react-hot-toast'

const WordScramble = ({ userData, onBack, selectedLevel }) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [gameLoading, setGameLoading] = useState(false)
  const [currentLevel, setCurrentLevel] = useState(selectedLevel)
  const [userLevel, setUserLevel] = useState('A1')
  const [words, setWords] = useState([])
  const [currentWordIndex, setCurrentWordIndex] = useState(0)
  const [scrambledWord, setScrambledWord] = useState('')
  const [userAnswer, setUserAnswer] = useState('')
  const [lives, setLives] = useState(3)
  const [score, setScore] = useState(0)
  const [gameCompleted, setGameCompleted] = useState(false)
  const [showExitDialog, setShowExitDialog] = useState(false)
  const [showLevelCompleteDialog, setShowLevelCompleteDialog] = useState(false)

  useEffect(() => {
    if (user && userData && selectedLevel) {
      setUserLevel(userData.level || 'A1')
      setCurrentLevel(selectedLevel)
      loadWords()
    }
  }, [user, userData, selectedLevel])

  useEffect(() => {
    if (selectedLevel) {
      setCurrentLevel(selectedLevel)
    }
  }, [selectedLevel])

  const loadWords = async () => {
    try {
      setLoading(true)
      console.log('Loading words for level:', userLevel, 'game level:', selectedLevel)

      if (!selectedLevel) {
        console.error('selectedLevel is undefined')
        toast.error('Level seçimi yapılmadı')
        return
      }

      const firestoreLevel = userLevel.toUpperCase()
      const fieldName = firestoreLevel === 'EXAM' ? 'wordpairs' : 'wordPairs'

      const levelDocRef = doc(db, 'words', firestoreLevel, 'levels', selectedLevel.toString())
      const levelDoc = await getDoc(levelDocRef)

      if (!levelDoc.exists()) {
        toast.error('Bu level için kelime bulunamadı')
        return
      }

      const data = levelDoc.data()
      const levelWords = data[fieldName] || []

      if (levelWords.length === 0) {
        toast.error('Bu level için kelime bulunamadı')
        return
      }

      console.log('Level words:', levelWords.length)
      setWords(levelWords)
      startGame(levelWords)
    } catch (error) {
      console.error('Error loading words:', error)
      toast.error('Kelimeler yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const shuffleWord = (word) => {
    const letters = word.split('')
    for (let i = letters.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[letters[i], letters[j]] = [letters[j], letters[i]]
    }
    return letters.join('')
  }

  const startGame = (wordList) => {
    if (!wordList || wordList.length === 0) {
      toast.error('Kelime bulunamadı')
      return
    }

    setCurrentWordIndex(0)
    setLives(3)
    setScore(0)
    setGameCompleted(false)
    setUserAnswer('')
    
    // İlk kelimeyi karıştır
    const firstWord = wordList[0].word
    setScrambledWord(shuffleWord(firstWord))
  }

  const handleSubmit = () => {
    if (gameLoading) return

    const currentWord = words[currentWordIndex]
    const isCorrect = userAnswer.toLowerCase().trim() === currentWord.word.toLowerCase().trim()

    if (isCorrect) {
      setScore(prev => prev + 10)
      toast.success('Doğru!')
      
      // Sonraki kelimeye geç
      if (currentWordIndex + 1 < words.length) {
        setTimeout(() => {
          const nextIndex = currentWordIndex + 1
          setCurrentWordIndex(nextIndex)
          setScrambledWord(shuffleWord(words[nextIndex].word))
          setUserAnswer('')
        }, 1000)
      } else {
        // Oyun tamamlandı
        setTimeout(() => {
          setGameCompleted(true)
          setShowLevelCompleteDialog(true)
        }, 1000)
      }
    } else {
      setLives(prev => prev - 1)
      setScore(prev => Math.max(0, prev - 5))
      toast.error('Yanlış!')
      
      // Can bitti mi kontrol et
      if (lives <= 1) {
        setTimeout(() => {
          setGameCompleted(true)
        }, 1000)
      }
    }
  }

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSubmit()
    }
  }

     const completeLevel = async () => {
     setShowLevelCompleteDialog(false)
     
     try {
       const firestoreLevel = userLevel.toUpperCase()
       
       // /gameProgress/A1/wordScramble/{userId}/completedLevels/level_{currentLevel}
       const levelDocRef = doc(db, 'gameProgress', firestoreLevel, 'wordScramble', user.uid, 'completedLevels', `level_${currentLevel}`)
       
       await setDoc(levelDocRef, {
         completed: true,
         completedAt: new Date(),
         score: score
       })
       
       // Puanları da kaydet
       const userScoreRef = doc(db, 'levels', firestoreLevel, 'leaderboard', user.uid)
       const userScoreDoc = await getDoc(userScoreRef)
       
       if (userScoreDoc.exists()) {
         const data = userScoreDoc.data()
         const completedLevels = data.completedLevels || []
         
         if (!completedLevels.includes(currentLevel)) {
           completedLevels.push(currentLevel)
         }
         
         await updateDoc(userScoreRef, {
           scrambleScore: increment(score),
           totalScore: increment(score),
           completedLevels: completedLevels
         })
       } else {
         await setDoc(userScoreRef, {
           username: userData?.username || user?.displayName || 'User',
           scrambleScore: score,
           totalScore: score,
           completedLevels: [currentLevel]
         })
       }
       
       
       
       console.log(`Level ${currentLevel} completed and saved to gameProgress collection with score: ${score}`)
       
       toast.success(`Level ${currentLevel} tamamlandı! Skorunuz: ${score}`)
       
       // Bir sonraki level'e geç
       const nextLevelNumber = currentLevel + 1
       onBack({ page: 'word-scramble', selectedLevel: nextLevelNumber })
     } catch (error) {
       console.error('Error saving score:', error)
       toast.error('Skor kaydedilirken hata oluştu')
       const nextLevelNumber = currentLevel + 1
       onBack({ page: 'word-scramble', selectedLevel: nextLevelNumber })
     }
   }

   

  const restartGame = () => {
    setCurrentWordIndex(0)
    setLives(3)
    setScore(0)
    setGameCompleted(false)
    setUserAnswer('')
    setScrambledWord(shuffleWord(words[0].word))
  }

  const exitGame = () => {
    setShowExitDialog(true)
  }

  const confirmExit = () => {
    setShowExitDialog(false)
    onBack()
  }

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={exitGame}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Word Scramble
            </Typography>
          </Toolbar>
        </AppBar>
        <Container maxWidth="md" sx={{ py: 3 }}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Yükleniyor...</Typography>
          </Box>
        </Container>
      </Box>
    )
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={exitGame}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Word Scramble
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            Level {currentLevel}
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3, pb: 10 }}>
        {/* Game Stats */}
        <Card sx={{ mb: 3, mx: 2 }}>
          <CardContent sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2 }}>
                  Skor: {score}
                </Typography>
                <Typography variant="h6">
                  Kelime: {currentWordIndex + 1}/{words.length}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                {[1, 2, 3].map((heart) => (
                  <Box key={heart} sx={{ ml: 0.5 }}>
                    {heart <= lives ? (
                      <HeartIcon sx={{ color: 'red', fontSize: 24 }} />
                    ) : (
                      <HeartBorderIcon sx={{ color: 'gray', fontSize: 24 }} />
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Game Content */}
        <Card sx={{ mx: 2, mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                Karışık Kelime:
              </Typography>
              <Typography 
                variant="h3" 
                sx={{ 
                  fontFamily: 'monospace',
                  letterSpacing: '0.2em',
                  color: '#1976d2',
                  fontWeight: 'bold',
                  mb: 2
                }}
              >
                {scrambledWord}
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Anlamı: {words[currentWordIndex]?.meaning}
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <TextField
                fullWidth
                label="Kelimeyi yazın"
                value={userAnswer}
                onChange={(e) => setUserAnswer(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={gameCompleted}
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={!userAnswer.trim() || gameCompleted}
                sx={{ minWidth: 120 }}
              >
                Kontrol Et
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Level Complete Dialog */}
        <Dialog open={showLevelCompleteDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ textAlign: 'center' }}>
            🏆 Level {currentLevel} Tamamlandı!
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ mb: 2 }}>
                Final Skor: {score}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Tebrikler! Level {currentLevel}'deki tüm kelimeleri başarıyla çözdünüz.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                         <Button 
               variant="outlined" 
               onClick={async () => {
                 // Level'i ve puanları kaydet ve sonra geri dön
                 try {
                   const firestoreLevel = userLevel.toUpperCase()
                   const levelDocRef = doc(db, 'gameProgress', firestoreLevel, 'wordScramble', user.uid, 'completedLevels', `level_${currentLevel}`)
                   
                   await setDoc(levelDocRef, {
                     completed: true,
                     completedAt: new Date(),
                     score: score
                   })
                   
                   // Puanları da kaydet
                   const userScoreRef = doc(db, 'levels', firestoreLevel, 'leaderboard', user.uid)
                   const userScoreDoc = await getDoc(userScoreRef)
                   
                   if (userScoreDoc.exists()) {
                     const data = userScoreDoc.data()
                     const completedLevels = data.completedLevels || []
                     
                     if (!completedLevels.includes(currentLevel)) {
                       completedLevels.push(currentLevel)
                     }
                     
                     await updateDoc(userScoreRef, {
                       scrambleScore: increment(score),
                       totalScore: increment(score),
                       completedLevels: completedLevels
                     })
                   } else {
                     await setDoc(userScoreRef, {
                       username: userData?.username || user?.displayName || 'User',
                       scrambleScore: score,
                       totalScore: score,
                       completedLevels: [currentLevel]
                     })
                   }
                   
                   console.log(`Level ${currentLevel} completed and saved to gameProgress collection with score: ${score}`)
                   toast.success(`Level ${currentLevel} tamamlandı! Puanınız: ${score}`)
                 } catch (error) {
                   console.error('Error saving score:', error)
                   toast.error('Skor kaydedilirken hata oluştu')
                 }
                 // Level seçim sayfasına dön
                 onBack({ page: 'word-scramble-level-selection', userLevel: userLevel })
               }}
               sx={{ mr: 2 }}
             >
               Level Seçimi
             </Button>
            <Button 
              variant="contained" 
              onClick={completeLevel}
            >
              Sonraki Level
            </Button>
          </DialogActions>
        </Dialog>

        {/* Game Over Dialog */}
        <Dialog open={gameCompleted && !showLevelCompleteDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ textAlign: 'center' }}>
            {lives > 0 ? '🎉 Oyun Tamamlandı!' : '😔 Oyun Bitti'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ mb: 2 }}>
                Skorunuz: {score}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {lives > 0 
                  ? `Tebrikler! ${words.length} kelimeyi başarıyla çözdünüz.`
                  : 'Canlarınız tükendi. Tekrar deneyin!'
                }
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button 
              variant="outlined" 
              onClick={async () => {
                // Eğer oyun başarıyla tamamlandıysa level'i ve puanları kaydet
                if (lives > 0) {
                  try {
                    const firestoreLevel = userLevel.toUpperCase()
                    const levelDocRef = doc(db, 'gameProgress', firestoreLevel, 'wordScramble', user.uid, 'completedLevels', `level_${currentLevel}`)
                    
                    await setDoc(levelDocRef, {
                      completed: true,
                      completedAt: new Date(),
                      score: score
                    })
                    
                    // Puanları da kaydet
                    const userScoreRef = doc(db, 'levels', firestoreLevel, 'leaderboard', user.uid)
                    const userScoreDoc = await getDoc(userScoreRef)
                    
                    if (userScoreDoc.exists()) {
                      const data = userScoreDoc.data()
                      const completedLevels = data.completedLevels || []
                      
                      if (!completedLevels.includes(currentLevel)) {
                        completedLevels.push(currentLevel)
                      }
                      
                      await updateDoc(userScoreRef, {
                        scrambleScore: increment(score),
                        totalScore: increment(score),
                        completedLevels: completedLevels
                      })
                    } else {
                      await setDoc(userScoreRef, {
                        username: userData?.username || user?.displayName || 'User',
                        scrambleScore: score,
                        totalScore: score,
                        completedLevels: [currentLevel]
                      })
                    }
                    
                    console.log(`Level ${currentLevel} completed and saved to gameProgress collection with score: ${score}`)
                    toast.success(`Level ${currentLevel} tamamlandı! Puanınız: ${score}`)
                  } catch (error) {
                    console.error('Error saving score:', error)
                    toast.error('Skor kaydedilirken hata oluştu')
                  }
                }
                // Level seçim sayfasına dön
                onBack({ page: 'word-scramble-level-selection', userLevel: userLevel })
              }}
              sx={{ mr: 2 }}
            >
              Level Seçimi
            </Button>
            <Button 
              variant="contained" 
              onClick={restartGame}
            >
              Tekrar Oyna
            </Button>
          </DialogActions>
        </Dialog>

        {/* Exit Confirmation Dialog */}
        <Dialog open={showExitDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Oyundan Çık</DialogTitle>
          <DialogContent>
            <Typography>
              Oyundan çıkmak istediğinizden emin misiniz? İlerlemeniz kaydedilmeyecek.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowExitDialog(false)}>
              İptal
            </Button>
            <Button onClick={confirmExit} color="error">
              Çık
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  )
}

export default WordScramble
